import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect, useCallback, useMemo } from 'react';
import { makeStyles } from '@mui/styles';
import { colors, Grid, IconButton } from '@mui/material';
import { Page, Header, Alert } from 'components';
import SpeedDial from '@mui/material/SpeedDial';
import SpeedDialIcon from '@mui/material/SpeedDialIcon';
import SpeedDialAction from '@mui/material/SpeedDialAction';
import AddBoxIcon from '@mui/icons-material/AddBox';
import FileOpenIcon from '@mui/icons-material/FileOpen';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';

import SiteTrackerTableUpdate from './components/SiteTrackerTableUpdate';
import SiteTrackerTableUNewEntries from './components/SiteTrackerTableNewEntries';
import SiteTrackerTable from './components/SiteTrackerTable';
import SiteTrackerDialog from './components/SiteTrackerDialog';
import ImportSiteTrackerModal from './components/ImportSiteTrackerModal';

import {
	ToggleSiteTrackerDialog,
	GetSiteTrackerTable,
	GetSiteTrackerUpdateTable,
	GetSiteTrackerLcsCreateTable,
	GetExportTracker,
	ExportSiteTrackerTemplate,
	ToggleImportModalVisibility,
	ToggleSiteTrackerUpdateVisibility,
	ToggleSiteTrackerLcsCreateVisibility
} from '../actions/SiteTrackerActions';

const useStyles = makeStyles(theme => ({
	root: {
		padding: theme.spacing(3)
	},
	container: {
		marginTop: theme.spacing(3)
	},
	tabs: {
		marginTop: theme.spacing(3)
	},
	content: {
		marginTop: theme.spacing(3)
	},
	paper: {
		padding: theme.spacing(1)
	},
	popover: {
		pointerEvents: 'none'
	},
	generate: {
		color: theme.palette.white,
		backgroundColor: colors.green[600],
		'&:hover': {
			backgroundColor: colors.green[900]
		}
	},
	alert: {
		'&:hover': {
			cursor: 'pointer'
		}
	}
}));

const SiteTracker = () => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const siteTrackerDialogVisibility = useSelector((state) => state.siteTrackerReducer.siteTrackerDialogVisibility);
	const selectedSiteId = useSelector((state) => state.siteTrackerReducer.selectedSiteId);
	const siteTrackerTable = useSelector((state) => state.siteTrackerReducer.siteTrackerTable);
	const importModalVisibility = useSelector((state) => state.siteTrackerReducer.importModalVisibility);
	const siteTrackerUpdateTable = useSelector((state) => state.siteTrackerReducer.siteTrackerUpdateTable);
	const siteTrackerCreateLCSTable = useSelector((state) => state.siteTrackerReducer.siteTrackerCreateLCSTable);
	const siteTrackerUpdateVisibility = useSelector((state) => state.siteTrackerReducer.siteTrackerUpdateVisibility);
	const siteTrackerLCSCreateVisibility = useSelector((state) => state.siteTrackerReducer.siteTrackerLCSCreateVisibility);
	const user = useSelector(state => state.session.user);

	const _onCreateNewTrackerEntryClick = () => {
		dispatch(ToggleSiteTrackerDialog(true, 0, ''));
	};

	const _onExportTrackerClick = () => {
		dispatch(GetExportTracker());
	};

	const _onImportTrackerClick = () => {
		dispatch(ToggleImportModalVisibility(true));
		// dispatch(ExportSiteTrackerTemplate());
	};

	const _onCloseImportModal = () => {
		dispatch(ToggleImportModalVisibility(false));
	};

	const _onCloseSiteTrackerDialog = (event, reason) => {
		if (reason !== 'backdropClick') {
			dispatch(ToggleSiteTrackerDialog(false, 0, ''));
		}
	};

	const actions = [
		{ icon: <AddBoxIcon />, name: 'Create New Entry', id: 'createNewTrackerEntry' },
		{ icon: <FileOpenIcon />, name: 'Export Tracker', id: 'exportTracker' },
		{ icon: <UploadFileRoundedIcon />, name: 'Import Tracker', id: 'importTracker' }
	];

	const _handleTrackerUpdateAlertClick = () => {
		dispatch(ToggleSiteTrackerUpdateVisibility(true));
	};

	const _handleTrackerCreateLCSAlertClick = () => {
		dispatch(ToggleSiteTrackerLcsCreateVisibility(true));
	};

	const _onCloseSiteTrackerUpdate = () => {
		dispatch(ToggleSiteTrackerUpdateVisibility(false));
	};

	const _onCloseSiteTrackerLcsCreate = () => {
		dispatch(ToggleSiteTrackerLcsCreateVisibility(false));
	};

	const _handleSpeedDialClick = (action) => {
		if (action.id === 'createNewTrackerEntry') {
			_onCreateNewTrackerEntryClick();
		} else if (action.id === 'exportTracker') {
			_onExportTrackerClick();
		} else if (action.id === 'importTracker') {
			_onImportTrackerClick();
		}
	};

	useEffect(() => {
		dispatch(GetSiteTrackerUpdateTable());
		dispatch(GetSiteTrackerLcsCreateTable());
		dispatch(GetSiteTrackerTable());
	}, []);

	return (
		<Page className={classes.root} title="Site Tracker">
			<Header module="Site Tracker" view="Site Tracker" />
			{siteTrackerUpdateTable.length > 0 && (
				<Alert
					className={classes.alert}
					variant="info"
					message={`There are ${siteTrackerUpdateTable.length} activation dates to be updated!`}
					onClick={() => { _handleTrackerUpdateAlertClick(); }}
				/>
			)}
			<Grid className={classes.container} container spacing={1}>
				<Grid item lg={12} xs={12}>
					{siteTrackerUpdateVisibility && user.role === 'Manager' && (
						<SiteTrackerTableUpdate
							open={siteTrackerUpdateVisibility}
							onClose={_onCloseSiteTrackerUpdate}
							data={siteTrackerUpdateTable}
						/>
					)}
				</Grid>
			</Grid>
			{siteTrackerCreateLCSTable.length > 0 && (
				<Alert
					className={classes.alert}
					variant="info"
					message={`There are ${siteTrackerCreateLCSTable.length} new jobs to be created!`}
					onClick={() => { _handleTrackerCreateLCSAlertClick(); }}
				/>
			)}
			<Grid className={classes.container} container spacing={1}>
				<Grid item lg={12} xs={12}>
					{siteTrackerLCSCreateVisibility && user.role === 'Manager' && (
						<SiteTrackerTableUNewEntries
							open={siteTrackerLCSCreateVisibility}
							onClose={_onCloseSiteTrackerLcsCreate}
							data={siteTrackerCreateLCSTable}
						/>
					)}
				</Grid>
			</Grid>
			<SpeedDial
				ariaLabel="SpeedDial"
				sx={{ position: 'absolute', bottom: 16, right: 16 }}
				icon={<SpeedDialIcon />}
			>
				{actions.map((action) => (
					<SpeedDialAction
						key={action.name}
						icon={action.icon}
						tooltipTitle={action.name}
						onClick={() => { _handleSpeedDialClick(action); }}
						cursor="pointer"
					/>
				))}
			</SpeedDial>
			<Grid className={classes.container} container spacing={3}>
				<Grid item lg={12} xs={12}>
					<SiteTrackerTable className={classes.results} data={siteTrackerTable} />
				</Grid>
			</Grid>
			<Grid className={classes.container} container spacing={3}>
				<Grid item lg={12} xs={12}>
					{siteTrackerDialogVisibility && (
						<SiteTrackerDialog
							onClose={_onCloseSiteTrackerDialog}
							open={siteTrackerDialogVisibility}
							siteTrackerId={selectedSiteId}
						/>
					)}
				</Grid>
			</Grid>
			<Grid className={classes.container} container spacing={3}>
				<Grid item lg={12} xs={12}>
					{importModalVisibility && (
						<ImportSiteTrackerModal
							onClose={_onCloseImportModal}
							open={importModalVisibility}
							title="Import Site Tracker"
						/>
					)}
				</Grid>
			</Grid>
		</Page>
	);
};

export default SiteTracker;
